/* ==========================================================================
   Spore & Bloom — motion
   File: assets/spore-bloom.js   (about 3 KB minified, no dependencies)

   Provides:
     - Scroll reveal for any element with [data-sb-reveal]
       ("" = fade and rise, "fade" = fade only, "media" = image settle).
       Optional stagger: style="--sb-delay: 120ms" on the element.
     - Gentle parallax for elements with class .sb-parallax and an optional
       data-sb-parallax="0.08" speed (0.02–0.2 is sensible).
     - Pause/play control for background videos, starting paused when the
       visitor prefers reduced motion.
     - Sticky add-to-cart bar on product pages (presses the real button).
     - Theme Editor support: re-initialises when sections are added,
       re-rendered or selected.

   Reveal and parallax are no-ops unless html.sb-motion is present, which
   snippets/spore-head.liquid only adds when the motion setting is on and
   the visitor has not asked for reduced motion.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.documentElement;
  var reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var revealObserver = null;
  var parallaxItems = new Set();
  var parallaxQueued = false;

  function motionEnabled() {
    return root.classList.contains('sb-motion') && !reducedQuery.matches;
  }

  /* ---------- Reveal ---------- */

  function reveal(el) {
    el.classList.add('is-revealed');
  }

  function revealAll(scope) {
    (scope || document).querySelectorAll('[data-sb-reveal]').forEach(reveal);
  }

  function initReveal(scope) {
    var els = (scope || document).querySelectorAll('[data-sb-reveal]:not(.is-revealed)');
    if (!els.length) return;

    if (!motionEnabled() || !('IntersectionObserver' in window)) {
      els.forEach(reveal);
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              reveal(entry.target);
              revealObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }

    els.forEach(function (el) {
      revealObserver.observe(el);
    });
  }

  /* ---------- Parallax ---------- */

  function updateParallax() {
    parallaxQueued = false;
    var viewport = window.innerHeight;

    parallaxItems.forEach(function (el) {
      if (!el.isConnected) {
        parallaxItems.delete(el);
        return;
      }
      // Measure the frame, not the moving element, so the maths is stable.
      var frame = el.parentElement || el;
      var rect = frame.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > viewport) return;

      var speed = parseFloat(el.getAttribute('data-sb-parallax')) || 0.08;
      var distanceFromCentre = rect.top + rect.height / 2 - viewport / 2;
      el.style.setProperty('--sb-parallax', (distanceFromCentre * -speed).toFixed(1) + 'px');
    });
  }

  function queueParallax() {
    if (parallaxQueued || !parallaxItems.size) return;
    parallaxQueued = true;
    window.requestAnimationFrame(updateParallax);
  }

  function initParallax(scope) {
    if (!motionEnabled()) return;
    (scope || document).querySelectorAll('.sb-parallax').forEach(function (el) {
      parallaxItems.add(el);
    });
    queueParallax();
  }

  function clearParallax() {
    parallaxItems.forEach(function (el) {
      el.style.removeProperty('--sb-parallax');
    });
    parallaxItems.clear();
  }

  window.addEventListener('scroll', queueParallax, { passive: true });
  window.addEventListener('resize', queueParallax, { passive: true });

  /* ---------- Background video pause / play (WCAG 2.2.2) ---------- */

  function setVideoState(button, video, playing) {
    button.setAttribute('aria-pressed', playing ? 'false' : 'true');
    var label = button.querySelector('[data-sb-video-label]');
    if (label) label.textContent = playing ? 'Pause background video' : 'Play background video';
    var pauseIcon = button.querySelector('[data-sb-icon-pause]');
    var playIcon = button.querySelector('[data-sb-icon-play]');
    if (pauseIcon) pauseIcon.toggleAttribute('hidden', !playing);
    if (playIcon) playIcon.toggleAttribute('hidden', playing);
  }

  function initVideoToggles(scope) {
    (scope || document).querySelectorAll('[data-sb-video-toggle]').forEach(function (button) {
      if (button.dataset.sbReady) return;
      button.dataset.sbReady = 'true';
      var section = button.closest('section');
      var video = section && section.querySelector('video');
      if (!video) return;

      // Respect reduced motion: start paused.
      if (reducedQuery.matches) {
        video.removeAttribute('autoplay');
        video.pause();
        setVideoState(button, video, false);
      }

      button.addEventListener('click', function () {
        if (video.paused) {
          video.play();
          setVideoState(button, video, true);
        } else {
          video.pause();
          setVideoState(button, video, false);
        }
      });
    });
  }

  /* ---------- Sticky add to cart (sections/spore-sticky-atc.liquid) ----------
     Shows once the page's own add-to-cart button has scrolled above the
     viewport, and presses that same button when used, so variants,
     subscriptions, the cart drawer and apps behave as normal. */

  function findMainAddButton() {
    return document.querySelector(
      '.product-form-wrapper .product--add-to-cart-button, ' +
      'form[action*="/cart/add"] button[type="submit"][name="add"]'
    );
  }

  function currentPriceText() {
    var prices = document.querySelectorAll('.product-form-wrapper .product-price-group .money');
    for (var i = 0; i < prices.length; i++) {
      if (!prices[i].closest('.compare-at-price')) return prices[i].textContent.trim();
    }
    return '';
  }

  function initStickyAtc(scope) {
    var bar = (scope || document).querySelector('[data-sb-sticky-atc]');
    if (!bar || bar.dataset.sbReady) return;
    var main = findMainAddButton();
    if (!main) return;
    bar.dataset.sbReady = 'true';

    var button = bar.querySelector('[data-sb-sticky-button]');
    var price = bar.querySelector('[data-sb-sticky-price]');

    // Flow may re-render the product form when the size changes, so always
    // look the button up again rather than trusting the first reference.
    function current() { return findMainAddButton() || main; }

    function sync() {
      var main = current();
      // Mirror the main button: label (e.g. "Sold out", "Pre-order") and state
      var label = (main.textContent || '').replace(/\s+/g, ' ').trim();
      if (label) button.textContent = label.split(' Added')[0].split(' loading')[0];
      var disabled = main.disabled || main.getAttribute('aria-disabled') === 'true';
      button.disabled = disabled;
      var text = currentPriceText();
      if (text && price) price.textContent = text;
    }

    function show(visible) {
      if (visible) {
        sync();
        bar.hidden = false;
        requestAnimationFrame(function () { bar.classList.add('is-visible'); });
      } else {
        bar.classList.remove('is-visible');
        bar.hidden = true;
      }
    }

    // Show only once the main button is above the screen, not before the
    // visitor has reached it. A throttled scroll check (rather than an
    // IntersectionObserver) also catches fast flings and anchor jumps that
    // skip straight past the button.
    var shown = false;
    var queued = false;
    function check() {
      queued = false;
      var visible = current().getBoundingClientRect().bottom < 0;
      if (visible !== shown) {
        shown = visible;
        show(visible);
      }
    }
    function queueCheck() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(check);
    }
    window.addEventListener('scroll', queueCheck, { passive: true });
    window.addEventListener('resize', queueCheck, { passive: true });
    check();

    button.addEventListener('click', function () {
      if (button.disabled) return;
      current().click();
    });

    // Keep price and label in step with variant changes (Flow re-renders
    // the price and toggles the button when the size changes)
    if ('MutationObserver' in window) {
      var watch = new MutationObserver(function () {
        if (!bar.hidden) sync();
      });
      var priceGroup = document.querySelector('.product-form-wrapper .product-price-group');
      if (priceGroup) watch.observe(priceGroup, { childList: true, subtree: true, characterData: true });
      watch.observe(main, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'], childList: true, subtree: true });
    }
  }

  /* ---------- Reduced-motion changes while the page is open ---------- */

  function onReducedChange() {
    if (reducedQuery.matches) {
      root.classList.remove('sb-motion');
      revealAll();
      clearParallax();
    }
  }

  if (reducedQuery.addEventListener) {
    reducedQuery.addEventListener('change', onReducedChange);
  } else if (reducedQuery.addListener) {
    reducedQuery.addListener(onReducedChange); // Safari < 14
  }

  /* ---------- Init ---------- */

  function init(scope) {
    initReveal(scope);
    initParallax(scope);
    initVideoToggles(scope);
    initStickyAtc(scope);
  }

  // Theme Editor: sections can be added, re-rendered or selected at any time.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:select', function (event) {
    // Make sure a section being edited is fully visible straight away.
    revealAll(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var section = event.target.closest('.shopify-section');
    revealAll(section || event.target);
  });

  // Public hook for later Spore & Bloom sections (and the failsafe check in
  // spore-head.liquid).
  window.SporeBloom = { init: init, reveal: revealAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }
})();
